import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { API_CONFIG } from '../../config/api';

const API_URL = API_CONFIG.SERVICE_CENTERS_URL;

const ServiceCenterManager = () => {
  const [serviceCenters, setServiceCenters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentServiceCenter, setCurrentServiceCenter] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', city: '', state: '', phone: '', email: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Admin Search & Filter State
  const [adminSearch, setAdminSearch] = useState('');
  const [adminStateFilter, setAdminStateFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchServiceCenters = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch data.');
      }
      const data = await response.json();
      setServiceCenters(Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceCenters();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleShowModal = (serviceCenter = null) => {
    if (serviceCenter) {
      setIsEditing(true);
      setCurrentServiceCenter(serviceCenter);
      setFormData(serviceCenter);
    } else {
      setIsEditing(false);
      setCurrentServiceCenter(null);
      setFormData({ name: '', address: '', city: '', state: '', phone: '', email: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isEditing ? `${API_URL}?id=${currentServiceCenter.id}` : API_URL;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (response.ok) {
        fetchServiceCenters();
        handleCloseModal();
      } else {
        throw new Error(result.message || 'Failed to save data.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service center?')) {
      try {
        const response = await fetch(`${API_URL}?id=${id}`, { method: 'DELETE' });
        const result = await response.json();
        if (response.ok) {
          fetchServiceCenters();
        } else {
          throw new Error(result.message || 'Failed to delete data.');
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Distinct states for filter
  const distinctStates = useMemo(() => {
    const s = new Set();
    serviceCenters.forEach(sc => {
      if (sc.state) s.add(sc.state.trim());
    });
    return Array.from(s).sort();
  }, [serviceCenters]);

  // Filtered centers
  const filteredList = useMemo(() => {
    let list = [...serviceCenters];
    if (adminStateFilter !== 'ALL') {
      list = list.filter(sc => sc.state === adminStateFilter);
    }
    if (adminSearch.trim()) {
      const q = adminSearch.toLowerCase();
      list = list.filter(sc => 
        (sc.name && sc.name.toLowerCase().includes(q)) ||
        (sc.city && sc.city.toLowerCase().includes(q)) ||
        (sc.state && sc.state.toLowerCase().includes(q)) ||
        (sc.address && sc.address.toLowerCase().includes(q)) ||
        (sc.phone && sc.phone.toLowerCase().includes(q))
      );
    }
    return list;
  }, [serviceCenters, adminStateFilter, adminSearch]);

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const paginatedCenters = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredList.slice(start, start + itemsPerPage);
  }, [filteredList, currentPage]);

  if (loading) {
    return <div className="d-flex justify-content-center mt-5"><Spinner animation="border" /></div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center my-4 flex-wrap gap-3">
        <div>
          <h2 className="mb-0">Service Center Management</h2>
          <small className="text-muted">Total: {serviceCenters.length} centers across {distinctStates.length} states</small>
        </div>
        <Button variant="primary" onClick={() => handleShowModal()}>
          <FaPlus className="me-2" /> Add Service Center
        </Button>
      </div>

      {error && !showModal && <Alert variant="danger">{error}</Alert>}

      {/* Search & State Filter Bar */}
      <div className="p-3 mb-4 bg-light rounded border">
        <Row className="g-3">
          <Col md={7}>
            <div className="input-group">
              <span className="input-group-text bg-white"><FaSearch /></span>
              <Form.Control
                type="text"
                placeholder="Search by city, center name, address, or phone..."
                value={adminSearch}
                onChange={(e) => {
                  setAdminSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </Col>
          <Col md={5}>
            <Form.Select
              value={adminStateFilter}
              onChange={(e) => {
                setAdminStateFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="ALL">All States ({distinctStates.length})</option>
              {distinctStates.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span>Showing <strong>{filteredList.length}</strong> matching centers</span>
        {totalPages > 1 && <span>Page {currentPage} of {totalPages}</span>}
      </div>

      <Table striped bordered hover responsive className="align-middle">
        <thead className="table-dark">
          <tr>
            <th style={{ width: '60px' }}>#</th>
            <th>Name</th>
            <th>Address</th>
            <th>City</th>
            <th>State</th>
            <th>Phone</th>
            <th style={{ width: '120px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedCenters.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-4 text-muted">
                No service centers match your filter.
              </td>
            </tr>
          ) : (
            paginatedCenters.map((sc, index) => (
              <tr key={sc.id}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td className="fw-semibold">{sc.name}</td>
                <td><small>{sc.address}</small></td>
                <td><span className="badge bg-secondary">{sc.city}</span></td>
                <td>{sc.state}</td>
                <td><small>{sc.phone || 'N/A'}</small></td>
                <td>
                  <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowModal(sc)}>
                    <FaEdit />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(sc.id)}>
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2 my-4">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            <FaChevronLeft className="me-1" /> Prev
          </Button>
          <span className="px-3">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next <FaChevronRight className="ms-1" />
          </Button>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Service Center' : 'Add New Service Center'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Service Center Name</Form.Label>
              <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Address</Form.Label>
              <Form.Control as="textarea" rows={2} name="address" value={formData.address} onChange={handleInputChange} required />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control type="text" name="city" value={formData.city} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control type="text" name="state" value={formData.state} onChange={handleInputChange} required />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number(s)</Form.Label>
                  <Form.Control type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="e.g. 9918222444 / 9876543210" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="e.g. support@example.com" />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-grid mt-3">
              <Button variant="primary" type="submit">
                {isEditing ? 'Save Changes' : 'Add Service Center'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ServiceCenterManager;
